import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
// @ts-ignore
import { useSlotGroup } from '@vibesites/runtime/react';
// @ts-ignore
import type { SlotGroupDefinition } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faXmark, faExpand, faCompress, faPlay, faPause, faGrip } from '@fortawesome/free-solid-svg-icons';

// ── Slot group definitions (one per photo slot) ────────────────────────

const NUM_SLOTS = 4;

function photoSlotDef(index: number): SlotGroupDefinition {
  return {
    label: `Photo ${index + 1}`,
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: `photo_gallery[${index}].image`,
      },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultField: `photo_gallery[${index}].alt_text`,
      },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

const SLOT_DEFS: Record<string, SlotGroupDefinition> = {};
for (let i = 0; i < NUM_SLOTS; i++) {
  SLOT_DEFS[`photolightbox.photo_${i}`] = photoSlotDef(i);
}

// ── Exported photo type ────────────────────────────────────────────────

export interface LightboxPhoto {
  src: string;
  alt?: string;
}

// ── Hook: resolve all photo slots ──────────────────────────────────────

function usePhotoSlots(): LightboxPhoto[] {
  const results: LightboxPhoto[] = [];
  for (let i = 0; i < NUM_SLOTS; i++) {
    const key = `photolightbox.photo_${i}`;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const slot = useSlotGroup(key, SLOT_DEFS[key]);
    if (slot.image) {
      results.push({ src: slot.image, alt: slot.alt || '' });
    }
  }
  return results;
}

// ── Component ──────────────────────────────────────────────────────────

interface PhotoLightboxProps {
  /** Explicit photos override; when omitted, uses the slot-group-bound photos. */
  photos?: LightboxPhoto[];
  initialIndex?: number;
  open: boolean;
  onClose: () => void;
  /** Extra class on the overlay, for caller-scoped presentation tweaks (e.g. framing transparent images). */
  overlayClassName?: string;
}

export function PhotoLightbox({ photos: photosProp, initialIndex = 0, open, onClose, overlayClassName }: PhotoLightboxProps) {
  const slotPhotos = usePhotoSlots();
  const photos = photosProp ?? slotPhotos;

  const [index, setIndex] = useState(initialIndex);
  const [showThumbs, setShowThumbs] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoplay, setAutoplay] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { setIndex(initialIndex); }, [initialIndex]);

  const total = photos.length;
  const prev = useCallback(() => setIndex(i => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIndex(i => (i + 1) % total), [total]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, prev, next]);

  useEffect(() => {
    if (autoplay && open) {
      autoplayRef.current = setInterval(next, 3000);
    }
    return () => { if (autoplayRef.current) clearInterval(autoplayRef.current); };
  }, [autoplay, open, next]);

  useEffect(() => {
    if (!open) {
      setAutoplay(false);
      setIsFullscreen(false);
    }
  }, [open]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      overlayRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Swipe gesture support for mobile
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) next();
    else prev();
  }, [next, prev]);

  // Preload adjacent images so navigation feels instant
  const adjacentSrcs = useMemo(() => {
    if (!open || total <= 1) return [];
    return [
      photos[(index + 1) % total]?.src,
      photos[(index - 1 + total) % total]?.src,
    ].filter(Boolean);
  }, [open, index, total, photos]);

  useEffect(() => {
    adjacentSrcs.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [adjacentSrcs]);

  if (!open || total === 0) return null;

  const photo = photos[index];

  return (
    <div
      ref={overlayRef}
      className={`lightbox-overlay${overlayClassName ? ` ${overlayClassName}` : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery"
    >
      {/* Top bar */}
      <div className="lightbox-topbar">
        <span className="lightbox-counter">{index + 1} / {total}</span>
        <div className="lightbox-controls">
          <button onClick={() => setAutoplay(a => !a)} aria-label={autoplay ? 'Pause slideshow' : 'Play slideshow'}>
            <FontAwesomeIcon icon={autoplay ? faPause : faPlay} />
          </button>
          <button onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
          </button>
          <button onClick={() => setShowThumbs(s => !s)} aria-label={showThumbs ? 'Hide thumbnails' : 'Show thumbnails'}>
            <FontAwesomeIcon icon={faGrip} />
          </button>
          <button onClick={onClose} aria-label="Close gallery">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>

      {/* Navigation arrows */}
      {total > 1 && (
        <>
          <button className="lightbox-arrow lightbox-arrow-left" onClick={prev} aria-label="Previous photo">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button className="lightbox-arrow lightbox-arrow-right" onClick={next} aria-label="Next photo">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </>
      )}

      {/* Main image — key forces React to mount a fresh <img> per photo. Click stage (gray area) to close. */}
      <div
        className="lightbox-stage"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }}
        aria-label="Close lightbox"
      >
        <VibesitesImage key={photo.src} url={photo.src} alt={photo.alt || ''} draggable={false} />
      </div>

      {/* Thumbnail strip */}
      {showThumbs && total > 1 && (
        <div className="lightbox-thumbs">
          {photos.map((p, i) => (
            <button
              key={i}
              className={`lightbox-thumb ${i === index ? 'active' : ''}`}
              onClick={() => setIndex(i)}
              aria-label={`View photo ${i + 1}`}
            >
              <VibesitesImage url={p.src} alt="" draggable={false} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Expose slot definitions so parent components (like AmenitiesList) can reuse them. */
export { SLOT_DEFS as PHOTO_SLOT_DEFS, NUM_SLOTS as PHOTO_NUM_SLOTS };

export default PhotoLightbox;
